/**
 * EthnicPath Virtual - Pricing Display (single source of truth)
 *
 * Everything the marketing surfaces need to render prices and checkout CTAs:
 * monthly prices, yearly (annual) totals, setup fees, and Stripe Payment
 * Links.
 *
 * PRICING V5 (2026-07-07)
 * -----------------------
 * Company-wide reprice. SMB tiers drop about 30% in both currencies and the
 * USD Business/Enterprise stickers are realigned to a proper FX ratio
 * (roughly 1.43x CAD over USD) instead of sharing the CAD numeral:
 *   SMB monthly  CAD 99 / 199 / 299        USD 69 / 139 / 199
 *   SMB yearly   CAD 1,049 / 2,099 / 3,199 USD 749 / 1,499 / 2,099
 *   Business     CAD 999 (unchanged)       USD 699
 *   Enterprise   CAD 1,499 (unchanged)     USD 999
 *   Two-way add-on (Standard): CAD +100 / USD +60 per month
 * CAD Business/Enterprise stickers were already clean, so their Stripe links
 * are UNCHANGED; every other link below is a v5 recreation (payment links are
 * immutable). All SMB tiers have NO setup fee in either currency.
 * (v5 left overage rates unchanged; they were then repriced in v6, below.)
 *
 * OVERAGE V6 (2026-07-08)
 * -----------------------
 * SMB overage reprice, locked by Kurt 2026-07-08. The per-additional-minute
 * rate becomes 0.25 (Starter) / 0.24 (Standard) / 0.23 (High-Volume), with the
 * SAME numerals in CAD and USD (replacing CAD 0.69/0.66/0.63 and USD
 * 0.49/0.47/0.45). Business (0.44) and Enterprise (0.35) overage is unchanged.
 * Plan prices are unchanged (pricing v5 stays). The old 0.45/min floor is
 * dead; the new floor is 0.23.
 *
 * TWO-CURRENCY MODEL (2026-07)
 * ----------------------------
 * EthnicPath is a CAD-first brand, but visitors can toggle the whole pricing
 * surface between CA$ (CAD) and US$ (USD). Every plan therefore carries a full
 * pricing block per currency: monthly price, friendly annual total, per-month
 * equivalent, marketing overage rate, setup fee, and the monthly / yearly
 * Stripe checkout links.
 *
 *  - CAD is the NATIVE currency: its Stripe Payment Links are the real, live,
 *    hardcoded links (verified). CAD is the default currency for every visitor
 *    except those geolocated to the US (see src/middleware.ts).
 *  - USD is the CROSS currency: live USD links exist in the EthnicPath Stripe
 *    account and are hardcoded as defaults below; per-plan / per-interval
 *    NEXT_PUBLIC_ env vars can still override them without a code change.
 *    We NEVER show a CAD checkout link under a USD price (never charge CAD
 *    while displaying USD).
 *
 * The Stripe webhook reads the billing currency from the Checkout Session
 * (session.currency) and the plan from metadata.tier_key; see the note in the
 * webhook handler.
 *
 * Billing periods:
 *  - Monthly: the classic price, billed every month.
 *  - Yearly:  one payment per year at ~10% off. The friendly annual totals
 *             were approved directly (close to but not mechanically
 *             round(monthly x 12 x 0.9)).
 *
 * Setup fees:
 *  - Small Business tiers (Starter / Standard / High-Volume): NO setup fee.
 *  - Business / Enterprise: one-time $300 setup fee (same numeric value in
 *    both currencies, quoted in the shown currency).
 *
 * Entitlements (minutes, agents, languages) stay in ./plans.ts; this file is
 * display + checkout only.
 */

export type BillingPeriod = "monthly" | "yearly";

export type PricingPlanKey =
  | "starter"
  | "standard"
  | "high-volume"
  | "business"
  | "enterprise";

/** Supported display / checkout currencies. CAD is native, USD is the cross. */
export type Currency = "CAD" | "USD";

/** The default currency for this brand (US visitors default to USD). */
export const DEFAULT_CURRENCY: Currency = "CAD";

/** Yearly billing discount, in percent (pay for a year, save 10%). */
export const YEARLY_DISCOUNT_PCT = 10;

/** One-time setup fee on Business / Enterprise (both currencies). SMB: none. */
export const SETUP_FEE = 300;

export interface PlanCurrencyPricing {
  key: PricingPlanKey;
  /** Monthly price. */
  price: number;
  /** Friendly annual total, billed once per year (approved directly). */
  yearlyTotal: number;
  /** Per-month equivalent shown in yearly mode: round(yearlyTotal / 12). */
  yearlyPerMonth: number;
  /** Marketing overage rate, per additional minute. */
  overage: number;
  /** One-time setup fee (0 for the SMB self-serve tiers). */
  setupFee: number;
  /**
   * Monthly checkout link. For the NATIVE currency (CAD) this is the live,
   * hardcoded Stripe Payment Link. For the CROSS currency (USD) it is the
   * live hardcoded link with an env-var override (see USD_URLS).
   */
  monthlyUrl?: string;
  /** Yearly checkout link. Same native/cross rules as monthlyUrl. */
  yearlyUrl?: string;
}

export type CurrencyPricingMap = Record<PricingPlanKey, PlanCurrencyPricing>;

/**
 * Live CAD (native) monthly Stripe Payment Links (metadata.tier_key +
 * billing_interval on the SMB ones for auto-provisioning). The three SMB links
 * are the v5 (2026-07-07) reprice recreations, no setup fee; Business and
 * Enterprise keep their original links (price unchanged) with the setup line
 * item.
 */
const CAD_MONTHLY_URLS: Record<PricingPlanKey, string> = {
  starter: "https://buy.stripe.com/28E6oH8CCaUW7k212wbZe0D",
  standard: "https://buy.stripe.com/eVq28r7yyfbc5bU3aEbZe0F",
  "high-volume": "https://buy.stripe.com/eVq28r066d340VEdPibZe0H",
  business: "https://buy.stripe.com/3cIcN52ee9QS1ZI9z2bZe0d",
  enterprise: "https://buy.stripe.com/7sY00j0669QS7k29z2bZe0e",
};

/**
 * Live CAD (native) yearly Stripe Payment Links, hardcoded (the links are
 * known and immutable, and carry metadata.billing_interval). The three SMB
 * links are v5 recreations at the new annual totals; Business and Enterprise
 * annual links are unchanged and include the one-time $300 CAD setup line
 * item.
 */
const CAD_YEARLY_URLS: Record<PricingPlanKey, string> = {
  starter: "https://buy.stripe.com/5kQbJ1aKK5ACcEm3aEbZe0E",
  standard: "https://buy.stripe.com/4gM00j4mm6EG5bU26AbZe0G",
  "high-volume": "https://buy.stripe.com/5kQdR9f10fbccEmfXqbZe0I",
  business: "https://buy.stripe.com/bJe00j7yy0gi1ZI3aEbZe0r",
  enterprise: "https://buy.stripe.com/00wdR98CC9QS5bUbHabZe0s",
};

/**
 * In-app "Upgrade to Enterprise" CAD Payment Link (no setup fee variant), used
 * by the portal upsell surfaces (SMS gateway, campaign reports). Enterprise
 * CAD price is unchanged in v5, so the link is still valid.
 */
export const ENTERPRISE_UPGRADE_CHECKOUT_URL =
  "https://buy.stripe.com/6oU4gz8CC1km1ZI5iMbZe0f";

/**
 * Cross-currency (USD) checkout links. Live v5 links (2026-07-07) in the
 * EthnicPath Stripe account are the hardcoded defaults; NEXT_PUBLIC_ env vars
 * can override each one without a code change (must be inlined at build time,
 * so each is read as a literal process.env reference).
 */
const USD_URLS: Record<PricingPlanKey, { monthly?: string; yearly?: string }> = {
  starter: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_STARTER_USD_URL || "https://buy.stripe.com/cNicN5f109QS7k24eIbZe0J",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_STARTER_USD_YEARLY_URL || "https://buy.stripe.com/5kQfZh3ii6EG6fY26AbZe0K",
  },
  standard: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_STANDARD_USD_URL || "https://buy.stripe.com/7sYdR9bOOgfg47Q5iMbZe0L",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_STANDARD_USD_YEARLY_URL || "https://buy.stripe.com/cNi4gz9GG4wy0VEdPibZe0M",
  },
  "high-volume": {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_USD_URL || "https://buy.stripe.com/9B614naKK7IK6fY6mQbZe0N",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_USD_YEARLY_URL || "https://buy.stripe.com/28E28r066fbc9sah1ubZe0O",
  },
  business: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_BUSINESS_USD_URL || "https://buy.stripe.com/28E8wP4mmd341ZI9z2bZe0P",
    yearly: process.env.NEXT_PUBLIC_STRIPE_BUSINESS_USD_YEARLY_URL || "https://buy.stripe.com/5kQcN51aa0gi6fY5iMbZe0Q",
  },
  enterprise: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_USD_URL || "https://buy.stripe.com/7sYcN50669QS8o69z2bZe0R",
    yearly: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_USD_YEARLY_URL || "https://buy.stripe.com/6oU00j5qqd34awedPibZe0S",
  },
};

/** CAD (native): the EthnicPath sheet, with live hardcoded Stripe links. */
const CAD_PRICING: CurrencyPricingMap = {
  starter: {
    key: "starter",
    price: 99,
    yearlyTotal: 1049,
    yearlyPerMonth: 87,
    overage: 0.25,
    setupFee: 0,
    monthlyUrl: CAD_MONTHLY_URLS.starter,
    yearlyUrl: CAD_YEARLY_URLS.starter,
  },
  standard: {
    key: "standard",
    price: 199,
    yearlyTotal: 2099,
    yearlyPerMonth: 175,
    overage: 0.24,
    setupFee: 0,
    monthlyUrl: CAD_MONTHLY_URLS.standard,
    yearlyUrl: CAD_YEARLY_URLS.standard,
  },
  "high-volume": {
    key: "high-volume",
    price: 299,
    yearlyTotal: 3199,
    yearlyPerMonth: 267,
    overage: 0.23,
    setupFee: 0,
    monthlyUrl: CAD_MONTHLY_URLS["high-volume"],
    yearlyUrl: CAD_YEARLY_URLS["high-volume"],
  },
  business: {
    key: "business",
    price: 999,
    yearlyTotal: 10699,
    yearlyPerMonth: 892,
    overage: 0.44,
    setupFee: SETUP_FEE,
    monthlyUrl: CAD_MONTHLY_URLS.business,
    yearlyUrl: CAD_YEARLY_URLS.business,
  },
  enterprise: {
    key: "enterprise",
    // Site-wide Enterprise price (partner page corrected to match).
    price: 1499,
    yearlyTotal: 16099,
    yearlyPerMonth: 1342,
    overage: 0.35,
    setupFee: SETUP_FEE,
    monthlyUrl: CAD_MONTHLY_URLS.enterprise,
    yearlyUrl: CAD_YEARLY_URLS.enterprise,
  },
};

/**
 * USD (cross): FX-aligned stickers (~1.43x under CAD), live v5 links with env
 * override (USD_URLS).
 */
const USD_PRICING: CurrencyPricingMap = {
  starter: {
    key: "starter",
    price: 69,
    yearlyTotal: 749,
    yearlyPerMonth: 62,
    overage: 0.25,
    setupFee: 0,
    monthlyUrl: USD_URLS.starter.monthly,
    yearlyUrl: USD_URLS.starter.yearly,
  },
  standard: {
    key: "standard",
    price: 139,
    yearlyTotal: 1499,
    yearlyPerMonth: 125,
    overage: 0.24,
    setupFee: 0,
    monthlyUrl: USD_URLS.standard.monthly,
    yearlyUrl: USD_URLS.standard.yearly,
  },
  "high-volume": {
    key: "high-volume",
    price: 199,
    yearlyTotal: 2099,
    yearlyPerMonth: 175,
    overage: 0.23,
    setupFee: 0,
    monthlyUrl: USD_URLS["high-volume"].monthly,
    yearlyUrl: USD_URLS["high-volume"].yearly,
  },
  business: {
    key: "business",
    price: 699,
    yearlyTotal: 7499,
    yearlyPerMonth: 625,
    overage: 0.44,
    setupFee: SETUP_FEE,
    monthlyUrl: USD_URLS.business.monthly,
    yearlyUrl: USD_URLS.business.yearly,
  },
  enterprise: {
    key: "enterprise",
    price: 999,
    yearlyTotal: 10699,
    yearlyPerMonth: 892,
    overage: 0.35,
    setupFee: SETUP_FEE,
    monthlyUrl: USD_URLS.enterprise.monthly,
    yearlyUrl: USD_URLS.enterprise.yearly,
  },
};

/** Two-currency pricing map: currency -> plan -> pricing block. */
export const CURRENCY_PRICING: Record<Currency, CurrencyPricingMap> = {
  CAD: CAD_PRICING,
  USD: USD_PRICING,
};

/** The pricing block for one plan in one currency. */
export function getPlanPricing(
  currency: Currency,
  key: PricingPlanKey
): PlanCurrencyPricing {
  return CURRENCY_PRICING[currency][key];
}

/** Where a CTA points when the chosen currency/interval has no live link. */
export function contactHref(currency: Currency): string {
  return `/contact?subject=${encodeURIComponent(`${currency} Billing`)}`;
}

/** Label for the "contact us instead" CTA in a given currency. */
export function contactLabel(currency: Currency): string {
  return `Contact us for ${currency} billing`;
}

/**
 * Resolve a checkout URL for a plan/interval/currency. Returns the live link
 * when present, otherwise null so callers render the graceful contact
 * fallback. A foreign-currency price is never sent to a native checkout.
 */
export function resolveCheckoutUrl(
  currency: Currency,
  key: PricingPlanKey,
  interval: BillingPeriod
): string | null {
  const plan = CURRENCY_PRICING[currency][key];
  return (interval === "yearly" ? plan.yearlyUrl : plan.monthlyUrl) ?? null;
}

/** Format a whole-dollar amount in the given currency, e.g. 1499 -> "$1,499". */
export function formatMoney(amount: number, currency: Currency): string {
  const locale = currency === "USD" ? "en-US" : "en-CA";
  return `$${amount.toLocaleString(locale)}`;
}

/** Format a per-minute overage rate, e.g. 0.25 -> "$0.25". */
export function formatRate(rate: number): string {
  return `$${rate.toFixed(2)}`;
}
